import { Injectable } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { HubConnectionState } from '@microsoft/signalr';
import { environment } from '../../environments/environment';
import { DomainEventBus, DomainEventType } from '../core/events/domain-event-bus';

@Injectable({
  providedIn: 'root'
})
export class SignalrPaymentStatusService {
  private hubConnection: signalR.HubConnection;

  public get connected(): boolean {
    return this.hubConnection.state == HubConnectionState.Connected;
  }

  public get disconnected(): boolean {
    return this.hubConnection.state == HubConnectionState.Disconnected;
  }

  constructor(private bus: DomainEventBus) {
    this.hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(environment.apiUrl + '/v1/public/client-notifications')
      .withAutomaticReconnect()
      .build();

    // Observe reconnect lifecycle
    this.hubConnection.onreconnecting((_err) => {
      this.bus.emit({ type: DomainEventType.SignalrState, state: 'reconnecting' });
    });
    this.hubConnection.onreconnected((_id) => {
      this.bus.emit({ type: DomainEventType.SignalrState, state: 'reconnected' });
    });
    this.hubConnection.onclose((_err) => {
      this.bus.emit({ type: DomainEventType.SignalrState, state: 'closed' });
    });
  }

  async connect(): Promise<void> {
    try {
      await this.hubConnection.start();
      this.bus.emit({ type: DomainEventType.SignalrState, state: 'connected' });
      console.log('Connected to SignalR hub');
    } catch (err) {
      console.error('Error connecting to SignalR hub', err);
      console.log('hubConnectionState: ' + this.hubConnection.state);
    }
  }

  async subscribe(group: string, callback: (a: any) => void): Promise<void> {
    try {
      await this.waitWhileHubReconnecting();
      if (!this.connected) return;
      await this.hubConnection!.invoke('Subscribe', group);
      this.hubConnection?.on('Send', (receivedMessage) => {
        callback(receivedMessage);
      });
    } catch (err) {
      console.error('Error subscribing to: ' + group, err);
      console.log('hubConnectionState: ' + this.hubConnection.state);
    }
  }

  public async waitWhileHubReconnecting(): Promise<void> {
    while (!this.connected && !this.disconnected) {
      await new Promise((resolve) => setTimeout(resolve, 500));
    }
  }

  public async poll(action: () => Promise<void>, stopPolling: () => boolean, delayIfConnected: number = 3000, period: number = 1000, callsLimit: number = 360): Promise<void> {
    await this.waitWhileHubReconnecting();
    await new Promise((resolve) => setTimeout(resolve, this.connected ? delayIfConnected : period));
    let iteration = 0;
    while (!stopPolling() && callsLimit > 0) {
      if (!document.hidden) {
        iteration++;
        this.bus.emit({ type: DomainEventType.SignalrPollIteration, iteration });
        await action();
        callsLimit--;
      }
      await new Promise((resolve) => setTimeout(resolve, period));
    }
  }

  async disconnect(): Promise<void> {
    try {
      await this.hubConnection.stop();
      this.bus.emit({ type: DomainEventType.SignalrState, state: 'closed' });
      console.log('Disconnected to SignalR hub');
    } catch (err) {
      console.error('Error disconnecting to SignalR hub:', err);
      console.log('hubConnectionState: ' + this.hubConnection.state);
    }
  }
}
